import React from 'react';
import { VerificationRequest } from '../../types';
import { CheckCircle, XCircle, Eye, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
interface VerificationQueueProps {
  requests: VerificationRequest[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onView: (id: string) => void;
}
export function VerificationQueue({
  requests,
  onApprove,
  onReject,
  onView
}: VerificationQueueProps) {
  const getRiskColor = (score: number) => {
    if (score < 20) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (score < 50) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  };
  const getRiskLabel = (score: number) => {
    if (score < 20) return 'Low Risk';
    if (score < 50) return 'Medium Risk';
    return 'High Risk';
  };
  return <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors" data-id="element-227">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-228">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate" data-id="element-229">
          Verification Queue
        </h3>
        <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" data-id="element-230">
          {requests.length} Pending
        </span>
      </div>

      <div className="overflow-x-auto" data-id="element-231">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-232">
          <thead className="bg-gray-50 dark:bg-gray-700/50" data-id="element-233">
            <tr data-id="element-234">
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-235">
                Tenant
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-236">
                Submitted
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-237">
                Documents
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-238">
                Risk Score
              </th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap" data-id="element-239">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-240">
            {requests.map((request, index) => <motion.tr key={request.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-241">
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap" data-id="element-242">
                  <div className="flex items-center gap-3" data-id="element-243">
                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-700 dark:text-blue-400 font-bold flex-shrink-0" data-id="element-244">
                      {request.tenantName.charAt(0)}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]" data-id="element-245">
                      {request.tenantName}
                    </div>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-246">
                  <div className="flex items-center gap-1" data-id="element-247">
                    <Clock className="h-4 w-4 flex-shrink-0" data-id="element-248" />
                    <span className="truncate" data-id="element-249">
                      {new Date(request.submittedAt).toLocaleDateString()}
                    </span>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-250">
                  {request.documents.length} files
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap" data-id="element-251">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${getRiskColor(request.riskScore)}`} data-id="element-252">
                    {getRiskLabel(request.riskScore)} ({request.riskScore})
                  </span>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-253">
                  <div className="flex justify-end gap-2" data-id="element-254">
                    <button onClick={() => onView(request.id)} className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 transition-colors" title="View Details" data-id="element-255">
                      <Eye className="h-5 w-5 flex-shrink-0" data-id="element-256" />
                    </button>
                    <button onClick={() => onApprove(request.id)} className="text-gray-400 hover:text-green-600 dark:hover:text-green-400 p-1 transition-colors" title="Approve" data-id="element-257">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" data-id="element-258" />
                    </button>
                    <button onClick={() => onReject(request.id)} className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1 transition-colors" title="Reject" data-id="element-259">
                      <XCircle className="h-5 w-5 flex-shrink-0" data-id="element-260" />
                    </button>
                  </div>
                </td>
              </motion.tr>)}
          </tbody>
        </table>
        {requests.length === 0 && <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-261">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500 dark:text-green-400" data-id="element-262" />
            <p data-id="element-263">All caught up! No pending verifications.</p>
          </div>}
      </div>
    </div>;
}